<template>
    <div :class="{ 'is-impersonating': isImpersonating }">
        <ImpersonationBanner />
        <div class="layout-container" :class="containerClass">
            <AppTopbar :should-hide-navigation="shouldHideNavigation" :has-side-nav="hasSideNav" />
            <AppSidebar v-if="user && hasSideNav" :sidebar-component="sidebarComponent" />
            <div class="layout-content-wrapper" :class="{ 'tw:pt-0!': !isTopbarVisible }">
                <div class="layout-content" :class="{ 'tw:rounded-tl-none! tw:h-screen!': !isTopbarVisible }">
                    <div class="layout-content-inner" :class="{ 'data-table-page': isFullBleedContent }">
                        <AppBreadcrumb v-if="!shouldHideNavigation" />
                        <router-view />
                        <AppFooter />
                    </div>
                </div>
            </div>
            <Toast />
            <AppConfig />
            <portal-target name="dashboard-customization-sidebar" />

            <NoteDrawer v-if="!shouldHideNavigation && hasNote" />

            <EdverumChat v-if="isStanbridge && !shouldHideNavigation && user" />

            <AlertDrawer v-if="user" />
        </div>
    </div>
</template>

<script setup>
import get from 'lodash/get.js';
import { computed, getCurrentInstance, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import AlertDrawer from '@/components/alerts/AlertDrawer.vue';
import EdverumChat from '@/components/chat/EdverumChat.vue';
import ImpersonationBanner from '@/components/impersonation/ImpersonationBanner.vue';
import NoteDrawer from '@/components/notes/NoteDrawer.vue';
import { useEdverumClient } from '@/composables/useEdverumClient.js';
import { isAttemptLockdownRouteName, isCourseEditRouteName } from '@/utilities/sidebarMenuRoute.js';
import AppBreadcrumb from './AppBreadcrumb.vue';
import AppConfig from './AppConfig.vue';
import AppFooter from './AppFooter.vue';
import AppSidebar from './AppSidebar.vue';
import AppTopbar from './AppTopbar.vue';
import { useLayout } from './composables/layout.js';

const { isCorporate, isStanbridge } = useEdverumClient();

const { layoutConfig, layoutState, isSidebarActive, isTopbarVisible } = useLayout();

const outsideClickListener = ref(null);
const store = useStore();
const route = useRoute();

const isFullBleedContent = computed(() =>
    route.matched.some(record => record.meta.dataTablePage || record.meta.fullWidthContent)
);

const props = defineProps({
    user: {
        type: Object,
        default: undefined,
    },
});

function isOutsideClicked(event) {
    const sidebarEl = document.querySelector('.layout-sidebar');
    const topbarEl = document.querySelector('.topbar-menubutton');

    return !(
        sidebarEl?.isSameNode(event.target) ||
        sidebarEl?.contains(event.target) ||
        topbarEl?.isSameNode(event.target) ||
        topbarEl?.contains(event.target)
    );
}

function bindOutsideClickListener() {
    if (!outsideClickListener.value) {
        outsideClickListener.value = event => {
            if (isOutsideClicked(event)) {
                layoutState.overlayMenuActive = false;
                layoutState.overlaySubmenuActive = false;
                layoutState.staticMenuMobileActive = false;
                layoutState.menuHoverActive = false;
                layoutState.configSidebarVisible = false;
            }
        };
        document.addEventListener('click', outsideClickListener.value);
    }
}

function unbindOutsideClickListener() {
    if (outsideClickListener.value) {
        document.removeEventListener('click', outsideClickListener.value);
        outsideClickListener.value = null;
    }
}

watch(isSidebarActive, newVal => {
    if (newVal) {
        bindOutsideClickListener();
    } else {
        unbindOutsideClickListener();
    }
});

watch(
    () => layoutState.staticMenuMobileActive,
    isActive => {
        if (window.innerWidth < 1200) {
            document.body.classList.toggle('blocked-scroll', isActive);
        }
    }
);

// Close the mobile/tablet drawer whenever the route changes so the menu does
// not stay open over the newly navigated page. Set the flag directly instead
// of calling closeMenu(), which would also collapse the desktop sidebar.
watch(
    () => route.path,
    () => {
        if (window.innerWidth < 1200) {
            layoutState.staticMenuMobileActive = false;
        }
    }
);

onBeforeUnmount(() => {
    unbindOutsideClickListener();
});

const isImpersonating = computed(() => store.getters['Impersonation/isImpersonating']);

const containerClass = computed(() => {
    return [
        {
            'layout-light': layoutConfig.layoutTheme === 'colorScheme' && !layoutConfig.darkTheme,
            'layout-dark': layoutConfig.layoutTheme === 'colorScheme' && layoutConfig.darkTheme,
            'layout-primary': !layoutConfig.darkTheme && layoutConfig.layoutTheme === 'primaryColor',
            'layout-slim': layoutConfig.menuMode === 'slim',
            'layout-slim-plus': layoutConfig.menuMode === 'slim-plus',
            'layout-static': layoutConfig.menuMode === 'static',
            'layout-overlay': layoutConfig.menuMode === 'overlay',
            'layout-overlay-active': layoutState.overlayMenuActive,
            'layout-mobile-active': layoutState.staticMenuMobileActive,
            'layout-static-inactive': layoutState.staticMenuDesktopInactive && layoutConfig.menuMode === 'static',
            // CSS hook so corporate-only layout overrides can be scoped (stanbridge frozen).
            'client-corporate': isCorporate.value,
            // Mirrors the sidebar's expanded state onto the container so the
            // corporate top bar (not a sibling of the sidebar, so the
            // `.layout-sidebar-expanded ~` selector can't reach it) can widen
            // its left offset in sync. Desktop `summarize` === `sidebarSummaryMode`.
            'layout-sidebar-expanded-active': isCorporate.value && !layoutState.sidebarSummaryMode,
        },
    ];
});
const sidebarComponent = computed(() => {
    // `Menu.sidebar` is null (not undefined) after `Menu/reset`, so a
    // `get(..., default)` backup never fires for it — leaving sidebarComponent
    // null and rendering an empty sidebar. Use `??` so both null and undefined
    // fall through to the store-level backup / `app-sidebar` default.
    const sidebar = store.state.Menu?.sidebar ?? get(store.state, 'sidebar', 'app-sidebar');

    if (route.path.startsWith('/courses/') || route.path.startsWith('/course-templates/')) {
        return 'app-sidebar';
    }

    return sidebar;
});

const hasActiveAttempt = computed(() => {
    return store.getters['User/hasActiveAttempt'] || route.name === 'automated-survey-attempt';
});

const mustChangePassword = computed(() => {
    return props.user && props.user?.must_change_password;
});

const shouldHideNavigation = computed(() => {
    return hasActiveAttempt.value || mustChangePassword.value;
});

const instance = getCurrentInstance();
const $isMobile = computed(() => instance?.appContext.config.globalProperties.$isMobile);
const $isTablet = computed(() => instance?.appContext.config.globalProperties.$isTablet);

const hasSideNav = computed(() => {
    if (shouldHideNavigation.value) return false;

    if (isAttemptLockdownRouteName(route.name)) return false;

    // Always show hamburger menu on mobile/tablet devices
    if ($isMobile.value || $isTablet.value) {
        return true;
    }

    if (
        sidebarComponent.value === 'user-sidebar' ||
        route.name === 'course-dashboard' ||
        route.name === 'course-template-view'
    ) {
        return true;
    }

    if (isCourseEditRouteName(route.name)) {
        return store.getters['Menu/hasMenu'];
    }

    return store.getters['Menu/hasMenu'] && !store.state.Menu.hidden;
});

const hasNote = computed(() => {
    return store.state.NoteDrawer.showNoteDrawer;
});

watch(
    hasSideNav,
    newVal => {
        if (!newVal) {
            layoutState.staticMenuDesktopInactive = true;
        } else {
            layoutState.staticMenuDesktopInactive = false;
        }
    },
    { immediate: true }
);
</script>

<style lang="scss"></style>
